@if (isBrowser) {
  <!-- Chat Icon Button -->
  <div class="fixed bottom-9 right-6 pt-3 z-[999] w-16 h-16 rounded-full cursor-pointer border-4 overflow-hidden border-accent transition-colors shadow-lg flex items-center justify-center"
    (click)="toggleChat()"
    [class.hidden]="isOpen()">
       <p-image
        src="assets/imgs/AI_Assistant_small.png"
        alt="AI Assistant"
        class="w-full h-full object-contain scale-[1.7]">
      </p-image>
  </div>

  <!-- Login Tooltip -->
  @if (showLoginTooltip()) {
    <div *transloco="let t; prefix: 'assistant'"
     class="fixed bottom-28 right-6 z-[1000] bg-gray-800 text-white px-4 py-3 rounded-lg shadow-xl max-w-xs animate-fade-in">
      <div class="flex items-start gap-2">
        <i class="pi pi-info-circle text-accent mt-0.5"></i>
        <p class="text-sm">{{t('chatCloseMassage')}}</p>
      </div>
      <div class="absolute -bottom-2 right-8 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-gray-800"></div>
    </div>
  }

  <!-- Chat Window -->
  @if (isOpen()) {
    <div *transloco="let t; prefix: 'assistant'"
      class="fixed z-[999] bg-white rounded-lg shadow-2xl flex flex-col transition-all duration-300"
      [ngClass]="{
        'bottom-4 right-4 w-80 h-[450px]': !isFullscreen(),
        'top-[4vh] left-[4vw] w-[90vw] h-[90vh]': isFullscreen()
      }">

      <!-- Header -->
      <div class="flex items-center justify-between py-3 px-4 border-b border-gray">
        <div class="flex items-center">
          <div class="w-12 pt-2 h-12 rounded-full flex items-center justify-center overflow-hidden">
            <p-image
              src="assets/imgs/AI_Assistant_small.png"
              alt="AI Assistant"
              class="w-full h-full object-contain scale-[1.7]">
            </p-image>
          </div>
          <div class="ml-2 pl-1">
            <p class="text-md font-medium">Personal AI Assistant</p>
            <div class="flex text-green">
            <div class="bg-green my-auto h-2 w-2 rounded-full"
                  [class.animate-pulse]="isTyping()"></div>
              <p class="px-1 text-sm">{{ isTyping() ? t('typing') : 'Online' }}</p>
            </div>
          </div>
        </div>
        <div class="flex items-center">
          <!-- Fullscreen Toggle Button -->
          <button
            (click)="toggleFullscreen()"
            class="text-gray-600 hover:text-gray-800 text-xl font-bold leading-none w-9 h-9 flex items-center justify-center"
            [title]="isFullscreen() ? 'Exit fullscreen' : 'Enter fullscreen'">
            <i [ngClass]="isFullscreen() ? 'pi pi-window-minimize' : 'pi pi-window-maximize'"></i>
          </button>
          <!-- Close Button -->
          <button
            (click)="closeChat()"
            class="text-gray-600 hover:text-gray-800 text-2xl font-normal leading-none w-8 h-8 flex items-center justify-center"
            title="Minimize chat">
            <span class="-mt-1">&minus;</span>
          </button>
        </div>
      </div>

      <!-- Messages Area -->
      <div *transloco="let t; prefix: 'assistant'"
       #messagesContainer class="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 relative">

        <!-- Empty State -->
        @if (messages().length === 0) {
          <div class="absolute inset-0 flex items-center justify-center p-6">
            <div class="text-center space-y-4 max-w-sm">
              <h3 class="text-lg font-medium text-gray-700 mb-4">{{t('howCanIHelp')}}</h3>
              <div class="space-y-3 text-left">
                <div class="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200">
                  <i class="pi pi-search text-accent mt-1"></i>
                  <p class="text-sm text-gray-600">{{t('findProduct')}}</p>
                </div>
                <div class="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200">
                  <i class="pi pi-info-circle text-accent mt-1"></i>
                  <p class="text-sm text-gray-600">{{t('answerQuestions')}}</p>
                </div>
                <div class="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200">
                  <i class="pi pi-shopping-cart text-accent mt-1"></i>
                  <p class="text-sm text-gray-600">{{t('helpWithOrders')}}</p>
                </div>
              </div>
            </div>
          </div>
        }

        <!-- Messages -->
        @for (message of messages(); track message) {
          <div
            [ngClass]="{'justify-end': message.isMine, 'justify-start': !message.isMine}"
            class="flex w-full">

            <!-- Message bubble -->
            <div
              [ngClass]="{
                'bg-accent text-white border-yellow-600': message.isMine,
                'bg-white text-gray-800 border-gray': !message.isMine
              }"
              class="max-w-[90%] px-3 py-2 break-words border rounded-lg inline-block">

              <p class="text-sm">{{ message.text }}</p>

              <!-- Product Card -->
              @if (message.productCards) {
                @for (card of message.productCards; track card) {
                  <div
                    class="mt-3 border border-gray rounded-lg p-2 bg-white cursor-pointer hover:shadow-md hover:border-accent transition-shadow"
                    (click)="navigateToProduct(card.slug)">
                    <div class="flex gap-3 items-center">
                      <!-- Image -->
                      <img
                        [src]="card.imageUrl | assetPreview:400:520"
                        [alt]="card.name"
                        (load)="scrollToBottomIfNeeded()"
                        class="w-16 h-16 object-cover rounded"
                      />

                      <!-- Product Info -->
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-800 truncate">
                          {{ card.name }}
                        </p>
                      </div>

                      <!-- Arrow Icon -->
                      <div class="text-right">
                        <i class="text-base font-semibold text-gray-800 pi pi-angle-right"></i>
                      </div>
                    </div>
                  </div>
                }
              }

              <!-- Timestamp -->
              <div class="text-[10px] mt-1"
                   [ngClass]="{'text-gray-100': message.isMine, 'text-gray-400': !message.isMine}">
                {{ message.timestamp | date:'HH:mm' }}
              </div>
            </div>
          </div>
        }
      </div>

      <!-- Input area -->
      <div class="p-2 border-t rounded-b-lg border-gray bg-white">
        <div
          class="flex gap-2"
          [ngClass]="{
            'w-full max-w-4xl mx-auto': isFullscreen(),
            'w-full': !isFullscreen()
          }">
          <div class="relative flex-1">
            <input pInputText
              type="text"
              [(ngModel)]="messageText"
              placeholder="Type your message..."
              class="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg bg-main focus:outline-none"
              (keyup.enter)="sendMessage()">

            <button
              (click)="sendMessage()"
              [disabled]="!messageText.trim()"
              class="absolute top-1/2 right-2 -translate-y-1/2 w-8 h-8 bg-accent hover:bg-amber-400 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg flex items-center justify-center transition-colors">
              <i class="pi pi-send text-white"></i>
            </button>
          </div>

          <button
            (click)="clearHistory()"
            [disabled]="messages().length === 0"
            class="w-8 h-8 bg-red mt-1 mr-1 hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg flex items-center justify-center transition-colors"
            title="clear chat history">
            <i class="pi pi-trash text-white"></i>
          </button>
        </div>
      </div>

    </div>
  }
}
